import { useState, useMemo } from 'react';
import SlideShell from './SlideShell';
import MarkdownRenderer from './MarkdownRenderer';

interface MatchPairItem {
  id: string;
  left: string;
  right: string;
}

interface MatchingSlideProps {
  slideNumber: number;
  totalSlides: number;
  title: string;
  subtitle: string;
  content: { pairs: MatchPairItem[] };
  lessonTitle?: string;
  lessonThumbnail?: string;
  onNext?: () => void;
  onBack?: () => void;
  onBackToLesson?: () => void;
  isCompleted?: boolean;
  onMarkComplete?: () => void;
}

const MatchingSlide = ({
  slideNumber,
  totalSlides,
  title,
  subtitle,
  content,
  lessonTitle,
  lessonThumbnail,
  onNext,
  onBack,
  onBackToLesson,
  isCompleted,
  onMarkComplete,
}: MatchingSlideProps) => {
  const pairs = content.pairs || [];
  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  const [selectedRight, setSelectedRight] = useState<number | null>(null);
  const [matches, setMatches] = useState<Record<number, number>>({});
  const [validatedMatches, setValidatedMatches] = useState<Record<number, boolean>>({});
  const [validated, setValidated] = useState(false);

  const leftItems = useMemo(() => pairs.map(pair => pair.left), [pairs]);
  
  // Shuffle right items while tracking original indices
  const shuffledRight = useMemo(() => {
    const items = pairs.map((pair, index) => ({ 
      text: pair.right, 
      originalIndex: index 
    }));
    // Fisher-Yates shuffle
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }, [pairs]);

  const handleLeftClick = (index: number) => {
    if (matches[index] !== undefined) return;
    setSelectedLeft(index);
    if (selectedRight !== null) {
      const newMatches = { ...matches, [index]: selectedRight };
      setMatches(newMatches);
      
      // Immediate validation
      const isCorrect = shuffledRight[selectedRight].originalIndex === index;
      setValidatedMatches({ ...validatedMatches, [index]: isCorrect });
      
      setSelectedLeft(null);
      setSelectedRight(null);
      setValidated(false);
    }
  };

  const handleRightClick = (index: number) => {
    if (Object.values(matches).includes(index)) return;
    setSelectedRight(index);
    if (selectedLeft !== null) {
      const newMatches = { ...matches, [selectedLeft]: index };
      setMatches(newMatches);
      
      // Immediate validation
      const isCorrect = shuffledRight[index].originalIndex === selectedLeft;
      setValidatedMatches({ ...validatedMatches, [selectedLeft]: isCorrect });
      
      setSelectedLeft(null);
      setSelectedRight(null);
      setValidated(false);
    }
  };

  const handleValidate = () => {
    setValidated(true);
  };

  const handleReset = () => {
    setMatches({});
    setValidatedMatches({});
    setSelectedLeft(null);
    setSelectedRight(null);
    setValidated(false);
  };

  const isMatchCorrect = (leftIndex: number): boolean => {
    const rightShuffledIndex = matches[leftIndex];
    if (rightShuffledIndex === undefined) return false;
    return shuffledRight[rightShuffledIndex].originalIndex === leftIndex;
  };

  const allCorrect = Object.keys(matches).length === leftItems.length && 
    Object.keys(matches).every(leftIdx => isMatchCorrect(Number(leftIdx)));

  return (
    <SlideShell
      lessonTitle={lessonTitle}
      lessonThumbnail={lessonThumbnail}
      slideNumber={slideNumber}
      totalSlides={totalSlides}
      subtitle={subtitle}
      onBackToLesson={onBackToLesson}
      onBack={onBack}
      onNext={onNext}
      showMarkComplete={true}
      isCompleted={isCompleted}
      onMarkComplete={validated && allCorrect ? onMarkComplete : undefined}
    >
      <h1 className="font-heading text-2xl mb-6 text-foreground">
        {title}
      </h1>

      <p className="text-base mb-8 text-muted-foreground">
        Match each term with its correct definition by clicking pairs.
      </p>

      <div className="grid grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-3">
          {leftItems.map((item, index) => (
            <button
              key={index}
              onClick={() => handleLeftClick(index)}
              disabled={matches[index] !== undefined}
              className={`w-full text-left py-2 px-3 rounded-[15px] text-sm transition-colors ${
                matches[index] !== undefined
                  ? validatedMatches[index]
                    ? 'bg-[#27c840] text-white'
                    : validatedMatches[index] === false
                    ? 'bg-destructive text-white'
                    : 'bg-primary/10 text-foreground border-2 border-primary'
                  : selectedLeft === index
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white border border-[#f0f0f0] hover:bg-[#fafafa]'
              }`}
            >
              <MarkdownRenderer content={item} />
            </button>
          ))}
        </div>

        {/* Right Column */}
        <div className="space-y-3">
          {shuffledRight.map((item, index) => (
            <button
              key={index}
              onClick={() => handleRightClick(index)}
              disabled={Object.values(matches).includes(index)}
              className={`w-full text-left py-2 px-3 rounded-[15px] text-sm transition-colors ${
                Object.values(matches).includes(index)
                  ? (() => {
                      const leftIdx = Object.entries(matches).find(([_, rightIdx]) => rightIdx === index)?.[0];
                      return leftIdx !== undefined && validatedMatches[Number(leftIdx)]
                        ? 'bg-[#27c840] text-white'
                        : leftIdx !== undefined && validatedMatches[Number(leftIdx)] === false
                        ? 'bg-destructive text-white'
                        : 'bg-primary/10 text-foreground border-2 border-primary';
                    })()
                  : selectedRight === index
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white border border-[#f0f0f0] hover:bg-[#fafafa]'
              }`}
            >
              <MarkdownRenderer content={item.text} />
            </button>
          ))}
        </div>
      </div>

      {Object.keys(matches).length === leftItems.length && !validated && (
        <button
          onClick={handleValidate}
          className="mt-8 bg-primary hover:bg-primary/90 text-primary-foreground rounded-[12px] px-6 py-3 font-medium transition-colors"
        >
          Check Answers
        </button>
      )}

      {validated && allCorrect && (
        <div className="mt-8 p-4 bg-[#27c840] text-white rounded-[15px] text-center">
          <p className="text-sm font-medium">
            Perfect! All items matched correctly.
          </p>
        </div>
      )}

      {validated && !allCorrect && (
        <div className="mt-8 p-4 bg-destructive text-white rounded-[15px] text-center">
          <p className="text-sm font-medium">
            Some matches are incorrect. Try again!
          </p>
        </div>
      )}

      {Object.keys(matches).length > 0 && (
        <button
          onClick={handleReset}
          className="mt-4 border border-input bg-background hover:bg-accent text-foreground rounded-[12px] px-6 py-3 font-medium transition-colors"
        >
          Reset All Matches
        </button>
      )}
    </SlideShell>
  );
};

export default MatchingSlide;
